import { deleteKnowledgeDocument, listKnowledgeDocuments, uploadKnowledgeDocument } from './knowledgeService';
import { authUserAtom, getClientById, getUserById } from '@consentropy/shared';
import { getDefaultStore } from 'jotai';
import type { ConversationTranscript, ConversationAnalysisRecord, DecisionArea, Member, Project } from '@consentropy/shared';
import type { DecisionArtifactVersion } from '../types/artifact';
import { getProjectById } from './projectService';
import { getMemberByProject } from './memberService';
import { getDecisionAreasByProject } from './decisionAreaService';

const authStore = getDefaultStore();

async function currentUserCompanyId(): Promise<string> {
  const user = authStore.get(authUserAtom);
  if (user?.companyId) return user.companyId;
  if (!user?.id) throw new Error('Cannot sync project context without an authenticated user');

  let record;
  try {
    record = await getUserById(user.id);
  } catch (error) {
    if (!user.username || user.username === user.id) throw error;
    record = await getUserById(user.username);
  }
  if (!record.companyId) throw new Error('Authenticated user has no company ID');
  return record.companyId;
}

function detail(label: string, value: string | undefined): string {
  return `- ${label}: ${value || 'Not provided'}`;
}

export function buildProjectContextMarkdown(
  project: Project,
  members: Member[],
  companyName: string,
  decisionAreas: DecisionArea[] = [],
): string {
  const lines = [
    `# Project: ${project.name}`,
    '',
    '## Project details',
    '',
    detail('Project ID', project.id),
    detail('Company ID', project.companyId),
    detail('Company name', companyName),
    detail('Client ID', project.clientId),
    detail('Name', project.name),
    detail('Description', project.description),
    detail('Phase', project.phase),
    detail('Status', project.status),
    detail('Value range', project.valueRange),
    detail('Start date', project.startDate),
    detail('Target end date', project.targetEndDate),
    detail('Created at', project.createdAt),
    detail('Updated at', project.updatedAt),
    detail('Created by', project.createdBy),
    '',
    '## Project members',
    '',
  ];

  if (members.length === 0) {
    lines.push('_No members added yet._');
  } else {
    for (const member of members) {
      lines.push(
        `### ${member.name}`,
        '',
        detail('Member ID', member.id),
        detail('Project ID', member.projectId),
        detail('Name', member.name),
        detail('Email', member.email),
        detail('Organisation', member.organisation),
        detail('Title', member.title),
        detail('Role', member.role),
        detail('Phone', member.phone),
        detail('Notes', member.notes),
        '',
      );
    }
  }

  lines.push('', '## Decision areas', '');
  if (decisionAreas.length === 0) {
    lines.push('_No decision areas defined yet._');
  } else {
    for (const area of decisionAreas) {
      lines.push(`### ${area.name}`, '', detail('Decision area ID', area.id), detail('Description', area.description), '#### Owners and roles');
      if (area.ownedAssignments.length === 0) lines.push('_No owners assigned._');
      else area.ownedAssignments.forEach((assignment) => lines.push(`- Member ID ${assignment.memberId}: ${assignment.role}`));
      lines.push('', '#### Decision boundaries');
      if (area.boundaries.length === 0) lines.push('_No boundaries defined._');
      else area.boundaries.forEach((boundary) => {
        lines.push(`- ${boundary.description}`);
        if (boundary.threshold) lines.push(`  - Threshold: ${boundary.threshold}`);
        if (boundary.approvalRequired !== undefined) lines.push(`  - Approval required: ${boundary.approvalRequired}`);
        if (boundary.escalationPath) lines.push(`  - Escalation path: ${boundary.escalationPath}`);
      });
      lines.push('');
    }
  }

  return lines.join('\n');
}

export async function ingestProjectContextToKnowledge(input: {
  project: Project;
  members: Member[];
  decisionAreas?: DecisionArea[];
  companyName?: string;
}): Promise<void> {
  if (import.meta.env.VITE_KNOWLEDGE_INGEST_DISABLED === 'true') return;
  // Project creation and member creation are already committed before this runs. A
  // transient company API failure must not prevent saving the project and member
  // context to Knowledge; the company ID remains in the document for later lookup.
  let companyName = input.companyName;
  if (!companyName) {
    try {
      companyName = (await getClientById(input.project.companyId)).name;
    } catch {
      companyName = 'Unavailable (company lookup failed)';
    }
  }
  companyName ||= 'Unavailable';
  const sourceRef = `project:${input.project.id}:context`;
  const uploaded = await uploadKnowledgeDocument({
    projectId: input.project.id,
    file: markdownToFile(
      buildProjectContextMarkdown(input.project, input.members, companyName, input.decisionAreas),
      `project-${input.project.id}-context.md`,
    ),
    sourceType: 'project-initial-info',
    sourceRef,
    tags: ['project-context', 'project-members', 'decision-areas'],
  });
  const currentPath = uploaded.path || uploaded.existingPath;
  const stale = (await listKnowledgeDocuments(input.project.id)).filter((document) =>
    document.frontmatter.sourceRef === sourceRef && document.path !== currentPath,
  );
  await Promise.all(stale.map((document) => deleteKnowledgeDocument(input.project.id, document.path)));
}

/** Rebuild the project snapshot after project, member, or decision area mutations. */
const projectContextSyncs = new Map<string, Promise<void>>();

export function refreshProjectContextToKnowledge(projectId: string): Promise<void> {
  const previous = projectContextSyncs.get(projectId) ?? Promise.resolve();
  const next = previous.catch(() => undefined).then(async () => {
    const companyId = await currentUserCompanyId();
    const [project, members, decisionAreas] = await Promise.all([
      getProjectById(projectId, companyId),
      getMemberByProject(projectId),
      getDecisionAreasByProject(projectId),
    ]);
    await ingestProjectContextToKnowledge({ project, members, decisionAreas });
  });
  projectContextSyncs.set(projectId, next);
  void next.finally(() => {
    if (projectContextSyncs.get(projectId) === next) projectContextSyncs.delete(projectId);
  }).catch(() => undefined);
  return next;
}

export async function removeProjectContextFromKnowledge(projectId: string): Promise<void> {
  if (import.meta.env.VITE_KNOWLEDGE_INGEST_DISABLED === 'true') return;
  const sourceRef = `project:${projectId}:context`;
  const documents = await listKnowledgeDocuments(projectId);
  await Promise.all(documents
    .filter((document) => document.frontmatter.sourceRef === sourceRef)
    .map((document) => deleteKnowledgeDocument(projectId, document.path)));
}

function markdownToFile(markdown: string, fileName: string): File {
  return new File([markdown], fileName, { type: 'text/markdown' });
}

function bulletList(items: string[] | undefined): string {
  if (!items || items.length === 0) return '_None_';
  return items.map((item) => `- ${item}`).join('\n');
}

// ---------- Transcript ----------

export function buildTranscriptMarkdown(transcript: ConversationTranscript): string {
  const lines: string[] = [`# Transcript: ${transcript.itemName}`, ''];
  if (transcript.summaryText) {
    lines.push('## Summary', '', transcript.summaryText, '');
  }
  const analysis = transcript.analysis;
  if (analysis) {
    lines.push('## Meeting Summary', '', analysis.meetingSummary, '');
    lines.push('## Key Decisions', '', bulletList(analysis.keyDecisions), '');
    lines.push('## Action Items', '', bulletList(analysis.actionItems), '');
    lines.push('## Risks or Concerns', '', bulletList(analysis.risksOrConcerns), '');
    lines.push('## Open Questions', '', bulletList(analysis.openQuestions), '');
    lines.push('## Next Steps', '', bulletList(analysis.nextSteps), '');
  }
  return lines.join('\n');
}

export async function ingestTranscriptToKnowledge(input: {
  projectId: string;
  conversationId: string;
  transcript: ConversationTranscript;
}): Promise<void> {
  const markdown = buildTranscriptMarkdown(input.transcript);
  const fileName = `transcript-${input.transcript.itemName || input.transcript.transcriptId}.md`;
  await uploadKnowledgeDocument({
    projectId: input.projectId,
    conversationId: input.conversationId,
    file: markdownToFile(markdown, fileName),
    sourceType: 'transcript',
    sourceRef: input.transcript.transcriptId,
  });
}

// ---------- Conversation analysis (CSE) ----------

export function buildAnalysisMarkdown(record: ConversationAnalysisRecord): string {
  const cse = record.cse;
  const title = record.title || record.conversationId;
  const lines: string[] = [`# Conversation Analysis: ${title}`, ''];
  if (!cse) return lines.join('\n');
  lines.push(`Overall rating: ${cse.overallRating}`, '');
  lines.push('## Key Recommendations', '', bulletList(cse.keyRecommendations), '');
  lines.push('## Indicator Scores', '');
  for (const [name, indicator] of Object.entries(cse.indicatorScores)) {
    lines.push(`### ${name} (score: ${indicator.score})`, '', bulletList(indicator.evidenceSnippets), '');
  }
  lines.push('## Topic Analysis', '');
  for (const topic of cse.topicAnalysis) {
    lines.push(`### ${topic.topic}`, '', topic.summary, '', bulletList(topic.evidenceSnippets), '');
  }
  return lines.join('\n');
}

export async function ingestAnalysisToKnowledge(input: {
  projectId: string;
  conversationId: string;
  record: ConversationAnalysisRecord;
}): Promise<void> {
  const markdown = buildAnalysisMarkdown(input.record);
  const fileName = `analysis-${input.conversationId}.md`;
  await uploadKnowledgeDocument({
    projectId: input.projectId,
    conversationId: input.conversationId,
    file: markdownToFile(markdown, fileName),
    sourceType: 'conversation',
    sourceRef: input.conversationId,
  });
}

// ---------- Artifact (finalized version) ----------

export function buildArtifactMarkdown(version: DecisionArtifactVersion): string {
  const lines: string[] = [`# Decision Artifact v${version.versionNumber}`, ''];
  const summary = version.changeSummary;
  if (summary) {
    lines.push('## Change Summary', '');
    lines.push('### Added', '', bulletList(summary.added), '');
    lines.push('### Updated', '', bulletList(summary.updated), '');
    lines.push('### Closed', '', bulletList(summary.closed), '');
  }
  for (const section of version.level1Sections ?? []) {
    lines.push(`## ${section.title}`, '', section.content, '');
  }
  return lines.join('\n');
}

export async function ingestArtifactToKnowledge(input: {
  projectId: string;
  version: DecisionArtifactVersion;
}): Promise<void> {
  const markdown = buildArtifactMarkdown(input.version);
  const fileName = `artifact-${input.version.decisionAreaId}-v${input.version.versionNumber}.md`;
  await uploadKnowledgeDocument({
    projectId: input.projectId,
    file: markdownToFile(markdown, fileName),
    sourceType: 'artifact',
    sourceRef: input.version.id,
  });
}

// ---------- Level 2 artifact (narrative bridge) ----------

export function buildLevel2ArtifactMarkdown(version: DecisionArtifactVersion): string {
  const lines: string[] = [`# Level 2 Artifact (Narrative) v${version.versionNumber}`, ''];
  const sections =
    version.generatedLevel2Sections && version.generatedLevel2Sections.length > 0
      ? version.generatedLevel2Sections
      : version.level2Sections ?? [];
  for (const section of sections) {
    lines.push(`## ${section.title}`, '', section.content, '');
  }
  return lines.join('\n');
}

export async function ingestLevel2ArtifactToKnowledge(input: {
  projectId: string;
  version: DecisionArtifactVersion;
}): Promise<void> {
  const markdown = buildLevel2ArtifactMarkdown(input.version);
  const fileName = `artifact-level2-${input.version.decisionAreaId}-v${input.version.versionNumber}.md`;
  await uploadKnowledgeDocument({
    projectId: input.projectId,
    file: markdownToFile(markdown, fileName),
    sourceType: 'artifact-level2',
    sourceRef: input.version.id,
  });
}

// ---------- Safe wrapper ----------

/** Knowledge indexing is best-effort: never let it break the primary flow. */
export async function safeKnowledgeIngest(task: () => Promise<void>): Promise<void> {
  try {
    await task();
  } catch (error) {
    console.warn('Knowledge indexing failed:', error);
  }
}
